import { useState, useEffect, useCallback, useRef } from 'react';
import { useUIStore } from '../store/uiStore';

type Breakpoint = 'mobile' | 'tablet' | 'desktop';

interface MediaQueryResult {
  breakpoint: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const MOBILE_QUERY = '(max-width: 639px)';
const TABLET_QUERY = '(min-width: 640px) and (max-width: 1023px)';
const DEBOUNCE_MS = 150;

function getBreakpoint(mobile: boolean, tablet: boolean): Breakpoint {
  if (mobile) return 'mobile';
  if (tablet) return 'tablet';
  return 'desktop';
}

export function useMediaQuery(): MediaQueryResult {
  const setSidebarMode = useUIStore((s) => s.setSidebarMode);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => {
    if (typeof window === 'undefined') return 'desktop';
    const mobile = window.matchMedia(MOBILE_QUERY).matches;
    const tablet = window.matchMedia(TABLET_QUERY).matches;
    return getBreakpoint(mobile, tablet);
  });

  const syncStore = useCallback(
    (bp: Breakpoint) => {
      switch (bp) {
        case 'desktop':
          setSidebarMode('persistent');
          setSidebarOpen(true);
          break;
        case 'tablet':
          setSidebarMode('overlay');
          setSidebarOpen(false);
          break;
        case 'mobile':
          setSidebarMode('fullscreen');
          setSidebarOpen(false);
          break;
      }
    },
    [setSidebarMode, setSidebarOpen],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mobileMql = window.matchMedia(MOBILE_QUERY);
    const tabletMql = window.matchMedia(TABLET_QUERY);

    const handleChange = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        const next = getBreakpoint(mobileMql.matches, tabletMql.matches);
        setBreakpoint((prev) => {
          if (prev !== next) {
            syncStore(next);
          }
          return next;
        });
      }, DEBOUNCE_MS);
    };

    mobileMql.addEventListener('change', handleChange);
    tabletMql.addEventListener('change', handleChange);

    return () => {
      mobileMql.removeEventListener('change', handleChange);
      tabletMql.removeEventListener('change', handleChange);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [syncStore]);

  return {
    breakpoint,
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet',
    isDesktop: breakpoint === 'desktop',
  };
}
